import { Component, Input, ElementRef, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-icon',
  standalone: false,
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./icon.component.scss']
})
export class IconComponent implements OnChanges {
  @Input() name!: string;
  @Input() size: string = '24px';
  @Input() color: string = '#fff';

  iconPath!: string;
  colorFilter!: string;

  constructor(private http: HttpClient, private elementRef: ElementRef) {}

  ngOnChanges() {
    if (this.name) {
      this.iconPath = `assets/icons/${this.name}.svg`;
      this.colorFilter = this.getColorFilter(this.color);
      this.loadSvg();
    }
  }

  private loadSvg() {
    this.http.get(this.iconPath, { responseType: 'text' }).subscribe(svgContent => {
      const container = this.elementRef.nativeElement;
      container.innerHTML = svgContent;
      const svg = container.querySelector('svg');
      if (svg) {
        svg.setAttribute('width', this.size);
        svg.setAttribute('height', this.size);
        
        const paths = svg.querySelectorAll('path, circle, rect, polygon, polyline, line, ellipse');
        
        let hasStrokeAttributes = false;
        let hasExplicitFill = false;
        
        paths.forEach((path: any) => {
          const stroke = path.getAttribute('stroke');
          const strokeWidth = path.getAttribute('stroke-width');
          const fill = path.getAttribute('fill');
          
          // Check if this icon uses stroke attributes (stroke OR stroke-width OR stroke="currentColor")
          if (stroke !== null || strokeWidth !== null) {
            hasStrokeAttributes = true;
          }
          
          // Check if this icon has explicit fill (but not fill="none")
          if (fill !== null && fill !== 'none') {
            hasExplicitFill = true;
          }
        });
        
        // Also check SVG root for stroke indicators
        const svgStroke = svg.getAttribute('stroke');
        const svgStrokeWidth = svg.getAttribute('stroke-width');
        if (svgStroke !== null || svgStrokeWidth !== null) {
          hasStrokeAttributes = true;
        }
        
        if (hasStrokeAttributes && !hasExplicitFill) {
          // This is a stroke-only icon
          svg.setAttribute('stroke', this.color);
          svg.setAttribute('fill', 'none');
          paths.forEach((path: any) => {
            if (!path.getAttribute('stroke')) {
              path.setAttribute('stroke', this.color);
            }
            path.setAttribute('fill', 'none');
          });
        } else if (hasExplicitFill || (!hasStrokeAttributes && !hasExplicitFill)) {
          // This is a fill-based icon OR no attributes at all (default fill)
          svg.setAttribute('fill', this.color);
          svg.removeAttribute('stroke');
          
          paths.forEach((path: any) => {
            path.setAttribute('fill', this.color);
            path.removeAttribute('stroke');
          });
        }
      }
    });
  }

  private getColorFilter(color: string): string {
    // Handle common colors
    if (color === '#fff' || color === 'white') {
      return 'brightness(0) invert(1)';
    }
    if (color === '#000' || color === 'black') {
      return 'brightness(0)';
    }
    
    // For hex colors
    const r = parseInt(color.slice(1, 3), 16) / 255;
    const g = parseInt(color.slice(3, 5), 16) / 255;
    const b = parseInt(color.slice(5, 7), 16) / 255;
    
    // Check if it's a grey color (r, g, b are approximately equal)
    const isGrey = Math.abs(r - g) < 0.1 && Math.abs(g - b) < 0.1 && Math.abs(r - b) < 0.1;
    
    if (isGrey) {
      // For grey colors, just adjust brightness
      const brightness = (r + g + b) / 3;
      return `brightness(0) invert(${brightness > 0.5 ? 1 : 0}) brightness(${brightness})`;
    }
    
    // For colored icons, use a simpler approach
    const brightness = (r + g + b) / 3;
    return `brightness(0) saturate(100%) invert(${brightness > 0.5 ? 1 : 0}) brightness(${brightness})`;
  }
}